/**
 * Session Tracker - Unified visitor session management
 *
 * Connects GA4 session → Device ID → User behavior → Email
 * Works with existing useDeviceId hook for fingerprinting
 */

// Storage keys
const SESSION_STORAGE_KEY = 'midash_session';
const DEVICE_STORAGE_KEY = 'midash_device_id'; // Same as useDeviceId.ts
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// API endpoint (will be created in backend)
const ANALYTICS_API = '/api/analytics/session';

export interface SessionData {
  session_id: string;
  device_id: string;
  ga_client_id: string | null;
  // Attribution
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string;
  landing_page: string;
  // Browser/Device info
  user_agent: string;
  // Timestamps
  first_visit: string;
  last_activity: string;
}

// Maps a landing-page pathname to the feature variant it represents.
// Keep in sync with FEATURE_VARIANTS in src/components/landing-new/heroVariants.ts.
const FEATURE_PATH_TO_VARIANT: Record<string, string> = {
  '/scanner': 'scanner',
  '/alerts': 'alerts',
  '/backtesting': 'backtesting',
  '/backtest': 'backtesting',
  '/watchlist': 'watchlist',
  '/byoai': 'byoai',
  '/portfolio': 'portfolio',
  '/research': 'research',
  '/ksa': 'ksa',
};

function inferFeatureFromPath(pathname: string): string | null {
  return FEATURE_PATH_TO_VARIANT[pathname] ?? null;
}

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  // Use crypto API if available, fallback to Math.random
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get device ID from localStorage (set by useDeviceId hook)
 * Returns a fallback if not yet available
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') return '';

  const storedId = localStorage.getItem(DEVICE_STORAGE_KEY);
  if (storedId) {
    return storedId;
  }

  // Fallback: generate a temporary ID (will be replaced by FingerprintJS)
  const tempId = `temp_${generateUUID()}`;
  console.log('[SessionTracker] Device ID not ready, using temp:', tempId);

  // Eagerly kick off FingerprintJS and update session once ready
  waitForDeviceIdAndUpgrade();

  return tempId;
}

/**
 * Wait for FingerprintJS to resolve, then upgrade the session's temp_ device_id.
 * Polls localStorage every 500ms for up to 10 seconds.
 */
async function waitForDeviceIdAndUpgrade(): Promise<void> {
  const MAX_ATTEMPTS = 20; // 20 * 500ms = 10 seconds max wait
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    await new Promise(resolve => setTimeout(resolve, 500));
    const realId = localStorage.getItem(DEVICE_STORAGE_KEY);
    if (realId) {
      // FingerprintJS has resolved — upgrade the stored session
      const stored = localStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        try {
          const session = JSON.parse(stored) as SessionData;
          if (session.device_id.startsWith('temp_')) {
            session.device_id = realId;
            localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
            // Push the upgraded device_id to the backend
            sendSessionToBackend(session);
            console.log('[SessionTracker] Upgraded temp device_id to:', realId);
          }
        } catch (e) {
          console.warn('[SessionTracker] Failed to upgrade session device_id:', e);
        }
      }
      return;
    }
  }
  console.warn('[SessionTracker] FingerprintJS did not resolve within 10s, temp_ device_id persists');
}

/**
 * Get GA4 client ID from cookie
 */
function getGA4ClientId(): string | null {
  if (typeof document === 'undefined') return null;

  const gaCookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('_ga='));

  if (gaCookie) {
    // GA cookie format: GA1.1.XXXXXXXXXX.XXXXXXXXXX
    const parts = gaCookie.split('.');
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }
  return null;
}

/**
 * Get UTM parameter from current session
 */
export function getUTM(param: 'source' | 'medium' | 'campaign' | 'term' | 'content'): string | null {
  const session = getSession();
  const key = `utm_${param}` as keyof SessionData;
  return session[key] as string | null;
}

/**
 * Get the current session ID
 */
export function getSessionId(): string {
  const session = getSession();
  return session.session_id;
}

/**
 * Get the full session object
 */
export function getSession(): SessionData {
  if (typeof window === 'undefined') {
    return createEmptySession();
  }

  const stored = localStorage.getItem(SESSION_STORAGE_KEY);

  if (stored) {
    try {
      const session = JSON.parse(stored) as SessionData;
      const lastActivity = new Date(session.last_activity).getTime();
      const now = Date.now();

      // If session hasn't expired, update last_activity and return
      if (now - lastActivity < SESSION_TIMEOUT_MS) {
        session.last_activity = new Date().toISOString();

        // Update device_id in case it was a temp ID and now we have the real one
        const currentDeviceId = localStorage.getItem(DEVICE_STORAGE_KEY);
        if (currentDeviceId && session.device_id.startsWith('temp_')) {
          session.device_id = currentDeviceId;
        }

        // Upgrade UTM params if the current URL has them and session doesn't.
        // This handles: user lands on "/" first, then navigates to "/taaft?utm_source=taaft"
        const urlParams = new URLSearchParams(window.location.search);
        let urlUtmSource = urlParams.get('utm_source');
        let urlUtmContent = urlParams.get('utm_content');

        // Campaign page auto-tagging: visiting /taaft implies utm_source=taaft
        // even if the URL has no explicit UTM params (e.g., user typed midash.ai/taaft directly)
        if (!urlUtmSource && window.location.pathname === '/taaft') {
          urlUtmSource = 'taaft';
        }

        // Feature landing auto-tagging: visiting /scanner, /alerts, etc. implies
        // utm_content=<feature>. Lets analytics answer "how many visits did the
        // scanner-specific ads generate?" even when an ad URL omits utm_content
        // (e.g., boosted social posts that strip query params).
        if (!urlUtmContent) {
          urlUtmContent = inferFeatureFromPath(window.location.pathname);
        }

        let mutated = false;

        if (urlUtmSource && !session.utm_source) {
          session.utm_source = urlUtmSource;
          session.utm_medium = urlParams.get('utm_medium') || session.utm_medium;
          session.utm_campaign = urlParams.get('utm_campaign') || session.utm_campaign;
          session.utm_term = urlParams.get('utm_term') || session.utm_term;
          session.utm_content = urlParams.get('utm_content') || urlUtmContent || session.utm_content;
          session.landing_page = window.location.pathname + window.location.search;
          mutated = true;
        } else if (urlUtmContent && !session.utm_content) {
          // Source already set on a previous page, but this visit reveals which
          // feature angle the user engaged with. Capture it without overwriting source.
          session.utm_content = urlUtmContent;
          mutated = true;
        }

        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
        if (mutated) {
          sendSessionToBackend(session);
        }
        return session;
      }
    } catch (e) {
      console.warn('[SessionTracker] Failed to parse stored session:', e);
    }
  }

  // Create new session
  return createNewSession();
}

/**
 * Create an empty session (for SSR)
 */
function createEmptySession(): SessionData {
  return {
    session_id: '',
    device_id: '',
    ga_client_id: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    referrer: '',
    landing_page: '',
    user_agent: '',
    first_visit: new Date().toISOString(),
    last_activity: new Date().toISOString(),
  };
}

/**
 * Create a new session with UTM capture
 */
function createNewSession(): SessionData {
  const urlParams = new URLSearchParams(window.location.search);

  const session: SessionData = {
    session_id: generateUUID(),
    device_id: getDeviceId(),
    ga_client_id: getGA4ClientId(),
    // Capture UTM params from URL
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content') || inferFeatureFromPath(window.location.pathname),
    // Capture referrer and landing page
    referrer: document.referrer || 'direct',
    landing_page: window.location.pathname + window.location.search,
    // Browser/Device info for analytics
    user_agent: navigator.userAgent,
    // Timestamps
    first_visit: new Date().toISOString(),
    last_activity: new Date().toISOString(),
  };

  // Store session
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

  console.log('[SessionTracker] New session created:', {
    session_id: session.session_id,
    utm_source: session.utm_source,
    utm_campaign: session.utm_campaign,
    referrer: session.referrer,
  });

  // Send session to backend (non-blocking)
  sendSessionToBackend(session);

  return session;
}

/**
 * Send session data to backend
 */
async function sendSessionToBackend(session: SessionData): Promise<void> {
  try {
    const response = await fetch(ANALYTICS_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(session),
    });

    if (!response.ok) {
      console.warn('[SessionTracker] Failed to send session to backend:', response.status);
    } else {
      const data = await response.json();
      console.log('[SessionTracker] Session sent to backend:', data);
    }
  } catch (error) {
    // Don't fail silently in dev, but don't break the app either
    console.warn('[SessionTracker] Error sending session to backend:', error);
  }
}

/**
 * Update session with converted email (called after waitlist signup)
 */
export function markSessionConverted(email: string): void {
  const session = getSession();

  // Send conversion update to backend
  fetch(ANALYTICS_API, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      session_id: session.session_id,
      waitlist_submitted: true,
      waitlist_email: email,
    }),
  }).catch(error => {
    console.warn('[SessionTracker] Error updating session conversion:', error);
  });
}

/**
 * Initialize session tracking - call this on app load
 * Returns the session data for immediate use
 */
export function initSessionTracking(): SessionData {
  const session = getSession();
  console.log('[SessionTracker] Session initialized:', session.session_id);
  return session;
}

/**
 * Get all attribution data for forms (waitlist, etc.)
 */
export function getAttributionData(): {
  session_id: string;
  device_id: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  landing_page: string;
  referrer: string;
} {
  const session = getSession();
  return {
    session_id: session.session_id,
    device_id: session.device_id,
    utm_source: session.utm_source,
    utm_medium: session.utm_medium,
    utm_campaign: session.utm_campaign,
    utm_term: session.utm_term,
    utm_content: session.utm_content,
    landing_page: session.landing_page,
    referrer: session.referrer,
  };
}
